import { useEffect, useState, useCallback } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ModeStandby from '@mui/icons-material/ModeStandby';
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined';
import { DropzoneFile, QuickbooksLogo, MicrosoftDynamics365, SageLogo } from '@lendica/components';
// import ReceiptIcon from '@mui/icons-material/Receipt';
// import { UploadList } from './UploadList';
// import { UnableToConnect } from './UnableToConnect';

import { DataConnection } from '@lendica/utils';
import { useDataConnectionDataContext } from '../DataConnectionData';

const services = [
    {
        logo: QuickbooksLogo,
        key: 'quickbooks',
    },
    {
        logo: MicrosoftDynamics365,
        key: 'microsoft',
    },
    {
        logo: SageLogo,
        key: 'sage',
    },
];

export const Accounting = ({
    company_id,
    env,
    connections,
}: {
    company_id: string | undefined;
    env: string;
    connections?: DataConnection[];
}) => {
    const [expanded, setExpanded] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<DropzoneFile[]>([]);
    const [connectedAccounts, setConnectedAccounts] = useState<string[]>([]);
    const [popupClosed, setPopupClosed] = useState(false);
    const [accountConnected, setAccountConnected] = useState(false);
    const { refreshConnections } = useDataConnectionDataContext();

    function launchPopup(service: string) {
        var win: any;
        var parameters = 'location=1,width=800,height=650';
        parameters += ',left=' + (screen.width - 800) / 2 + ',top=' + (screen.height - 650) / 2;

        // Launch Popup
        setPopupClosed(false);
        if (env === 'idev') {
            win = window.open(
                `https://micro-oauth-dev.herokuapp.com/oauth/${service}?company_id=${company_id}`,
                'connectPopup',
                parameters
            );
        } else if (env === 'prod') {
            win = window.open(
                `https://oauth.app.golendica.com/oauth/${service}?company_id=${company_id}`,
                'connectPopup',
                parameters
            );
        }

        let interval = setInterval(() => {
            if (win.closed) {
                console.log('closed');
                setPopupClosed(true);
                clearInterval(interval);
            }
        }, 1000);
    }

    const handleFileUpload = (file: any) => {
        setUploadedFiles(file);
    };

    useEffect(() => {
        const list = Array.isArray(connections) ? connections : [];
        const connected = services
            .filter(service => list.some((x: any) => x.active && x.saas_name === service.key))
            .map(service => service.key);

        setConnectedAccounts(connected);
        setAccountConnected(!!connected.length);
    }, [connections]);

    useEffect(() => {
        if (popupClosed) {
            refreshConnections();
        }
    }, [popupClosed, refreshConnections]);

    const handleClick = (service: string, isConnected: boolean) => {
        if (!isConnected) {
            launchPopup(service);
        }
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                '&:before': {
                    display: 'none',
                },
                'borderRadius': 1,
                'boxShadow': 'none',
                'paddingTop': 1,
                'background': '##EDEDF4',
                'border': '1px solid #EDEDF4',
                'mt': 1,
                'mb': 1,
                'paddingBottom': expanded ? 0 : 1,
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                color="#FFF"
                sx={{
                    border: 'none',
                    boxShadow: 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptOutlined color="primary" />
                    <Typography fontWeight={500} ml={2}>
                        Accounting
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingX: '1rem',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {accountConnected && <CheckCircleIcon color="success" />}
                </Box>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    padding: '0',
                }}
            >
                <Box>
                    {services.map((service, index) => {
                        const Logo = service.logo;
                        const isConnected = connectedAccounts.includes(service.key);

                        return (
                            <Box
                                key={index}
                                sx={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem 1rem',
                                    background: '#f2f2f8',
                                    borderRadius: 1,
                                    marginY: 1,
                                    marginX: 2,
                                    cursor: 'pointer',
                                    height: 78,
                                }}
                                component="div"
                                onClick={() => handleClick(service.key, isConnected)}
                            >
                                <Logo />
                                {isConnected ? (
                                    <Box sx={{ display: 'flex' }}>
                                        <Typography mr={1}>connected</Typography>
                                        <ModeStandby color="success" />
                                    </Box>
                                ) : (
                                    <SyncAltIcon />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
